import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Plus, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { DateRangeFilter } from "../components/shared/DateRangeFilter";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
// import { ReconciliationPanel } from "../components/routes/ReconciliationPanel"; // hidden per request 2026-10-20
import { UnassignedDeliveriesPanel } from "../components/routes/UnassignedDeliveriesPanel";
import { CustomerRouteSearch } from "../components/routes/CustomerRouteSearch";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useListState } from "../hooks/useListState";
import { formatDate, formatDistanceKm, formatDurationHm, todayLocal as today } from "../lib/utils";
import { distanceSourceMeta, packingStateMeta } from "../lib/statusMaps";

const PAGE_SIZE = 20;
const STATUS_FILTERS = [
  { value: "all", label: "All Packing" }, { value: "not_started", label: "Not Started" },
  { value: "started", label: "Started" }, { value: "done", label: "Done" },
];

export default function Routes() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useListState("routes.dateRange", { from: today(), to: today() });
  const [driverId, setDriverId] = useListState("routes.driverId", "all");
  const [statusFilter, setStatusFilter] = useListState("routes.statusFilter", "all");
  const [page, setPage] = useListState("routes.page", 0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const queryClient = useQueryClient();

  const driversQuery = useQuery({
    queryKey: ["routes-drivers"],
    queryFn: async () => (await apiClient.get("/drivers", { params: { limit: 200 } })).data.items,
    refetchOnMount: "always",
  });
  const drivers = driversQuery.data ?? [];

  const listQuery = useQuery({
    queryKey: ["routes-list", { from: dateRange.from, to: dateRange.to, driverId, statusFilter, page }],
    queryFn: async () => {
      const { data } = await apiClient.get("/routes", {
        params: {
          date_from: dateRange.from || undefined,
          date_to: dateRange.to || undefined,
          driver_id: driverId !== "all" ? driverId : undefined,
          packing_state: statusFilter !== "all" ? statusFilter : undefined,
          limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        },
      });
      return data;
    },
    placeholderData: keepPreviousData,
    // Always refetch when returning to this tab so changes made inside a route
    // (driver change, packing, reorder, delete) show immediately instead of
    // after the 45s global stale window.
    refetchOnMount: "always",
  });
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const error = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load routes.") : null;

  const filterKey = `${dateRange.from}|${dateRange.to}|${driverId}|${statusFilter}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  useEffect(() => { setSelectedIds([]); }, [listQuery.data]);

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => apiClient.delete("/routes/bulk-delete", { data: { route_ids: ids } }).then((r) => r.data),
    onSuccess: (data) => {
      if (data.deleted.length) toast.success(`Deleted ${data.deleted.length} route(s) — their stops are back in the unassigned pool.`);
      if (data.skipped.length) {
        toast.warning(`Skipped ${data.skipped.length}: ${data.skipped.map((s) => `#${s.id} (${s.reason})`).join(", ")}`);
      }
      if (!data.deleted.length && !data.skipped.length) {
        toast.info("No routes were deleted.");
      }
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      // A route delete frees its stops back to the unassigned pool, so refresh
      // routes and the delivery list too.
      queryClient.invalidateQueries({ queryKey: ["routes-list"] });
      queryClient.invalidateQueries({ queryKey: ["deliveries-list"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not delete selected routes.")),
  });

  const isRange = dateRange.from !== dateRange.to || !dateRange.from;

  const columns = [
    { key: "id", header: "Route #", className: "text-text-muted" },
    ...(isRange ? [] : [{ key: "date", header: "Date", render: (r) => formatDate(r.date) }]),
    { key: "driver_name", header: "Driver", subtitle: true, render: (r) => r.driver_name || "Unassigned" },
    { key: "status", header: "Packing Status", render: (r) => { const m = packingStateMeta(r.packing_state); return <StatusBadge label={m.label} variant={m.variant} />; } },
    { key: "deliveries", header: "Deliveries", render: (r) => `${r.deliveries_done}/${r.deliveries_total}` },
    { key: "distance", header: "Distance", render: (r) => (
      <div className="flex flex-col items-start gap-0.5">
        <span>{formatDistanceKm(r.distance)}</span>
        <StatusBadge label={distanceSourceMeta(r.distance_source).label} variant={distanceSourceMeta(r.distance_source).variant} className="w-fit text-[10px]" data-testid={`routes-row-distance-source-badge-${r.id}`} />
      </div>
    ) },
    { key: "duration", header: "Duration", render: (r) => formatDurationHm(r.duration) },
  ];

  return (
    <div data-testid="routes-page">
      <PageHeader
        title="Routes"
        description="Optimized delivery routes and driver assignments."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" asChild data-testid="manage-drivers-link">
              <Link to="/drivers"><Users size={16} strokeWidth={1.5} /> Manage Drivers</Link>
            </Button>
            <Button variant="accent" asChild data-testid="create-route-button">
              <Link to="/routes/builder"><Plus size={16} strokeWidth={2} /> Plan Routes</Link>
            </Button>
          </div>
        }
      />

      <div className="mb-4">
        <CustomerRouteSearch filters={{ from: dateRange.from, to: dateRange.to, driverId, packingState: statusFilter }} />
      </div>

      {/* Data Quality panel hidden per request (2026-10-20). Re-enable by restoring <ReconciliationPanel />. */}
      {/* <ReconciliationPanel /> */}
      <UnassignedDeliveriesPanel date={today()} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <DateRangeFilter from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="w-48" data-testid="route-driver-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Drivers</SelectItem>
            {drivers.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="route-status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <Button variant="danger" onClick={() => setBulkDeleteOpen(true)} data-testid="route-bulk-delete-button">
            <Trash2 size={16} strokeWidth={1.5} /> Delete Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      <DataTable
        testId="routes-table" columns={columns} rows={items} loading={listQuery.isPending} error={error}
        onRetry={() => listQuery.refetch()} onRowClick={(row) => navigate(`/routes/${row.id}`)}
        groupKey={isRange ? (row) => row.date : undefined}
        renderGroupHeader={isRange ? (date) => formatDate(date) : undefined}
        emptyTitle="No routes found" emptyDescription="Try a different date range or filter, or create a new route."
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
      />
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testId="routes-pagination" />
      <ConfirmDialog
        open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedIds.length} route${selectedIds.length === 1 ? "" : "s"}?`}
        description="Only routes with zero delivered stops will be deleted — their stops return to the unassigned pool for that date. Routes with delivered stops are skipped."
        confirmLabel="Delete Routes" variant="danger" loading={bulkDeleteMutation.isPending} onConfirm={() => bulkDeleteMutation.mutate(selectedIds)}
        testId="route-bulk-delete-confirm-dialog"
      />
    </div>
  );
}
